type Translate = (key: string, fallback: string) => string;

export type EmptyStateInput = {
    /** The list tab, as it appears in the `filter` query parameter. */
    filter: string;
    hasSearchOrFilters: boolean;
    inVinSession: boolean;
};

/**
 * Picks the message shown when the publications table has no rows.
 *
 * An empty Recently Viewed or Favorites tab usually means the user has not
 * opened or starred anything yet, so telling them to adjust a search they never
 * made reads as a broken screen. Inside a VIN session the same tabs can also be
 * empty because nothing on the list applies to that vehicle.
 *
 * @param input the tab, and what the user has entered or opened
 * @param t the i18next translate function
 * @returns the description for the table's empty state
 */
export const getPublicationsEmptyState = (input: EmptyStateInput, t: Translate): string => {
    const noMatches = t(
        'stisPublications:emptyState.noMatches',
        "Try adjusting your search or filters to find what you're looking for"
    );

    if (input.hasSearchOrFilters) {
        return noMatches;
    }

    if (input.filter === 'recently-viewed') {
        return input.inVinSession
            ? t(
                  'stisPublications:emptyState.recentlyViewedVehicle',
                  'None of your recently viewed publications apply to this vehicle'
              )
            : t(
                  'stisPublications:emptyState.recentlyViewed',
                  'No recently viewed publications. The last 10 publications you open show up here'
              );
    }

    if (input.filter === 'favorites') {
        return input.inVinSession
            ? t('stisPublications:emptyState.favoritesVehicle', 'None of your favorites apply to this vehicle')
            : t(
                  'stisPublications:emptyState.favorites',
                  'No favorites yet. Select the star on a publication to keep it here'
              );
    }

    return noMatches;
};
